import React, { useContext, useMemo } from 'react'
import { getTranslate } from 'react-localize-redux'
import { connect } from 'react-redux'
import { User, getEmail, getFirstname, getInstitutionsName, getLanguage, getLastConnection, getLastname, getRole } from '../../../utils/entities/user'
import { faEdit, faKey, faStethoscope } from '@fortawesome/pro-solid-svg-icons'
import { OrderBy, InstitutionRoles } from '../../../utils/constants'
import Cookie from 'js-cookie'
import { InstitutionGroupContext } from '../../../Providers/InstitutionGroupProvider'
import { GlobalFiltersContext } from '../../../Providers/GlobalFiltersProvider'
import { Action, DeleteAction } from '../../../Components/antd/Tables/DataTable/utils/actions'
import { buildFilters, Filter } from '../../../Components/antd/Tables/DataTable/utils/tableFilter'
import SelectInstitutionFilter from '../../../Components/antd/Tables/DataTable/utils/SelectInstitutionFilter'
import DataTable from '../../../Components/antd/Tables/DataTable/DataTable'

const mapStateToProps = state => ({ t: getTranslate(state.locale) })

const UsersTable = ({ loading, parameters, users, onParametersChange, onSelectUser, onDeleteUser, onSelectUserPassword, onSelectUserSectors, t }) => {
  const institutionGroupContext = useContext(InstitutionGroupContext)
  const { selectedInstitution, setSelectedInstitution } = useContext(GlobalFiltersContext)

  const maxHeight = useMemo(() => `calc(100vh - ${Cookie.get('switch_user_id') ? '240px' : '199px'})`, [Cookie.get('switch_user_id')])
  const columns = useMemo(() => [
    { title: t('users_view.column_title.email'), orderBy: OrderBy.EMAIL, key: getEmail },
    { title: t('users_view.column_title.lastname'), orderBy: OrderBy.LASTNAME, key: getLastname },
    { title: t('users_view.column_title.firstname'), orderBy: OrderBy.FIRSTNAME, key: getFirstname },
    { title: t('users_view.column_title.language'), orderBy: OrderBy.LANGUAGE, key: u => t('languages.' + getLanguage(u)) },
    { title: t('users_view.column_title.role'), orderBy: OrderBy.ROLE, key: u => t(getRole(u)) },
    {
      title: t('users_view.column_title.institution'),
      orderBy: OrderBy.INSTITUTION,
      key: u => {
        const name = getInstitutionsName(u)

        return name.length > 25 ? `${name.slice(0, 22)}...` : name
      },
      tooltip: getInstitutionsName
    },
    { title: t('users_view.column_title.last_connection'), orderBy: OrderBy.LAST_CONNECTION, key: getLastConnection }
  ], [t, getEmail, getLastname, getFirstname, getLanguage, getRole, getInstitutionsName])
  const actions = useMemo(() => [
    new Action({ title: 'users_view.actions.edit_user', icon: faEdit, onClick: onSelectUser }),
    new Action({
      title: 'users_view.actions.set_user_coordinate_sectors',
      icon: faStethoscope,
      onClick: onSelectUserSectors,
      disabled: d => d.roles.includes(InstitutionRoles.INSTITUTION_GROUP_ADMIN)
    }),
    // External accounts (institution SSO): their password is managed by their SSO, so the backend
    // would answer 409. The button is disabled for them, like in the other user tables.
    new Action({ title: 'users_view.actions.reset_user_password', icon: faKey, onClick: onSelectUserPassword, disabled: d => d.isExternal }),
    new DeleteAction({ onDelete: onDeleteUser })
  ], [onSelectUser, onDeleteUser, onSelectUserPassword, onSelectUserSectors])
  const filters = useMemo(() => buildFilters([
    new Filter({
      key: 'institution',
      label: t('common.institution'),
      component: SelectInstitutionFilter,
      props: { institutions: institutionGroupContext?.institutions, value: selectedInstitution, onChange: setSelectedInstitution }
    })
  ]), [institutionGroupContext?.institutions, selectedInstitution, setSelectedInstitution, t])

  return (
    <div style={{ height: maxHeight }}>
      <DataTable
        actions={actions}
        columns={columns}
        data={users.data}
        filters={filters}
        loading={loading}
        metadata={users.meta}
        parameters={parameters}
        onCreate={() => onSelectUser(new User())}
        onParametersChange={onParametersChange}
      />
    </div>
  )
}

export default connect(mapStateToProps)(UsersTable)
